/**
 * GraphBuilder — fluent API for constructing BehaviorGraph data.
 *
 * IMPORTANT: All target parameters MUST be SceneObject references, never raw
 * strings. Names are NOT unique or stable in the scene hierarchy — only
 * SceneObject refs guarantee correct identity. The builder resolves refs to
 * relative TransformPaths from the context object for serialization.
 *
 * Usage:
 *   const graph = new GraphBuilder(contextObj)
 *       .onSelect()
 *           .playAnimation(modelObj, 'Walk')
 *       .build();
 *
 *   const graph = new GraphBuilder(contextObj)
 *       .onSelect()
 *           .toggleActive(panelObj)
 *       .onHover()
 *           .enter().setActive(highlightObj, true)
 *           .exit().setActive(highlightObj, false)
 *       .build();
 *
 *   // flowFrom() lets you branch from an existing node:
 *   const builder = new GraphBuilder(contextObj);
 *   const hover = builder.onHover();
 *   hover.enter().setActive(a, true).setActive(b, true);
 *   hover.exit().setActive(a, false).setActive(b, false);
 *
 *   // Data nodes for random color:
 *   const b = new GraphBuilder(contextObj);
 *   const r = b.addRandomNode(0, 1);
 *   const g = b.addRandomNode(0, 1);
 *   const bl = b.addRandomNode(0, 1);
 *   const color = b.addCombine3Node(r, g, bl);
 *   b.onStart().setPropertyFrom(targetObj, 'color', color);
 */

import { GraphOps } from '../../scenedata/behaviors/behaviorgraphdata';
import type { GraphData, GraphNode, Declaration, GraphEvent, GraphVariable, TypeSignature } from '../../scenedata/behaviors/behaviorgraphdata';
import type { SceneObject } from '../../core/SceneObject';
import type { TimeTransition } from '../../scenedata/behaviors/propertiesdata';
import { easingToControlPoints } from '../../core/canopy/TransitionUtils';
import { expandIntervalMacro, expandMacro, INTERVAL_CONTINUE_SOCKET, INTERVAL_DONE_SOCKET, TOGGLE_ACTIVE_MACRO_DEF, TOGGLE_ACTIVE_TARGET_PARAM } from './macros';
import type { IntervalMacroNodes } from './macros';
import { compileGraphExpression, type ExpressionType, type GraphExpressionValue } from './graphExpression';

// ============================================================================
// Internal types
// ============================================================================

interface NodeEntry {
    op: string;
    config: Record<string, unknown[]>;
    values: Record<string, { type: number; value: unknown[] } | { node: number; socket: string }>;
    flows: Record<string, { node: number; socket: string }>;
    x: number;
    y: number;
    /** Optional author-supplied editor metadata (ignored by the runtime). */
    metadata?: Record<string, unknown>;
}

/** Builder type indices (before compaction): 0=bool, 1=float, 2=string, 3=float3, 4=float4, 5=float2. */
const BUILDER_SIGNATURES = ['bool', 'float', 'string', 'float3', 'float4', 'float2'] as const;

// ============================================================================
// GraphBuilder
// ============================================================================

export class GraphBuilder {
    private _nodes: NodeEntry[] = [];
    private _variables: GraphVariable[] = [];
    private _events: GraphEvent[] = [];
    private _x = 0;
    private _context: SceneObject;

    constructor(context: SceneObject) {
        this._context = context;
    }

    /** Compile typed scalar syntax into editable native nodes; no runtime script.
     * Reads use scope/ancestors. clamp(min,x,max) follows Canopy. Optional expected
     * type rejects mismatches before adding nodes. Use with setPropertyFrom/branch.
     */
    expression(source: string, scope: SceneObject = this._context, expected?: ExpressionType): GraphExpressionValue {
        return compileGraphExpression(this, source, scope, expected);
    }

    /**
     * @internal Resolve a SceneObject to a relative path from the context.
     * Never accept raw strings — names are not unique or stable.
     */
    _targetPath(target: SceneObject): string {
        return this._context.traceScene.getRelativePath(target, this._context);
    }

    // ── Events ──

    /**
     * Add an On Select event. Returns a flow builder for chaining actions.
     * @param metadata Optional author editor metadata attached to the event node
     *   (preserved through serialization, ignored by the runtime).
     */
    onSelect(metadata?: Record<string, unknown>): FlowChain {
        return this._addEventNode(GraphOps.EVENT_ON_SELECT, metadata);
    }

    /** Add an On Start event. Returns a flow builder for chaining actions. */
    onStart(metadata?: Record<string, unknown>): FlowChain {
        return this._addEventNode(GraphOps.EVENT_ON_START, metadata);
    }

    /**
     * Add On Hover In/Out events. Returns a hover builder with enter/exit chains.
     * Optional author metadata is attached to the hover-in (primary) event node.
     */
    onHover(metadata?: Record<string, unknown>): HoverChain {
        const inNode = this._addNode(GraphOps.EVENT_ON_HOVER_IN, {}, metadata);
        const outNode = this._addNode(GraphOps.EVENT_ON_HOVER_OUT, {});
        return new HoverChain(this, inNode, outNode);
    }

    /** Add an On Proximity event. */
    onProximity(target: SceneObject, distance: number, metadata?: Record<string, unknown>): ProximityChain {
        const nodeIndex = this._addNode(GraphOps.TRACE_ON_PROXIMITY, {
            target: [this._targetPath(target)],
        }, metadata);
        this._setValue(nodeIndex, 'threshold', distance);
        return new ProximityChain(this, nodeIndex);
    }

    /** Add an On Tick event (fires every frame). Returns a FlowChain for the `out` signal. */
    onTick(): FlowChain {
        return this._addEventNode(GraphOps.EVENT_ON_TICK);
    }

    /** Add an On State Change event. Returns a FlowChain for the `out` signal. */
    onStateChange(target: SceneObject): FlowChain {
        const nodeIndex = this._addNode(GraphOps.TRACE_ON_STATE_CHANGE, {
            target: [this._targetPath(target)],
        });
        return new FlowChain(this, nodeIndex, 'out');
    }

    // ── Properties ──

    /**
     * Add a getProperty data node. Returns the node index so it can be
     * wired as a value source (e.g. via FlowChain.setPropertyFrom).
     */
    addGetPropertyNode(target: SceneObject, property: string): number {
        return this._addNode(GraphOps.TRACE_GET_PROPERTY, {
            target: [this._targetPath(target)],
            property: [property],
        });
    }

    /** Add a getState data node. Returns the node index for wiring as a data source. */
    addGetStateNode(target: SceneObject): number {
        return this._addNode(GraphOps.TRACE_GET_STATE, {
            target: [this._targetPath(target)],
        });
    }

    /** Add a time data node (play-mode elapsed seconds). Returns the node index. */
    addTimeNode(): number {
        return this._addNode(GraphOps.TRACE_TIME, {});
    }

    /** Add a getActiveIndex data node for a Pages container. Returns the node index. */
    addPagesGetActiveIndexNode(target: SceneObject): number {
        return this._addNode(GraphOps.PAGES_GET_ACTIVE_INDEX, { target: [this._targetPath(target)] });
    }

    /** Add a getPageCount data node for a Pages container. Returns the node index. */
    addPagesGetPageCountNode(target: SceneObject): number {
        return this._addNode(GraphOps.PAGES_GET_PAGE_COUNT, { target: [this._targetPath(target)] });
    }

    // ── Transform data nodes ──

    /** Add a getPosition data node. Returns the node index. */
    addGetPositionNode(target: SceneObject): number {
        return this._addNode(GraphOps.POINTER_GET, {
            target: [this._targetPath(target)],
            property: ['translation'],
        });
    }

    /** Add a getRotation data node. Returns the node index. */
    addGetRotationNode(target: SceneObject): number {
        return this._addNode(GraphOps.POINTER_GET, {
            target: [this._targetPath(target)],
            property: ['rotation'],
        });
    }

    /** Add a getScale data node. Returns the node index. */
    addGetScaleNode(target: SceneObject): number {
        return this._addNode(GraphOps.POINTER_GET, {
            target: [this._targetPath(target)],
            property: ['scale'],
        });
    }

    // ── Data nodes ──

    /** Add a Get Active data node. Returns the node index. */
    addGetActiveNode(target: SceneObject): number {
        return this._addNode(GraphOps.TRACE_GET_ACTIVE, { target: [this._targetPath(target)] });
    }

    /** Add a Get Child Count data node. Returns the node index. */
    addGetChildCountNode(target: SceneObject): number {
        return this._addNode(GraphOps.TRACE_GET_CHILD_COUNT, { target: [this._targetPath(target)] });
    }

    /** Add a NOT data node wired to a source. Returns the node index. */
    addNotNode(source: number, socket = 'value'): number {
        const node = this._addNode(GraphOps.MATH_NOT, {});
        this._setValueRef(node, 'a', source, socket);
        return node;
    }

    /** Add a Floor data node wired to a source. Returns the node index. */
    addFloorNode(source: number, socket = 'value'): number {
        const node = this._addNode(GraphOps.MATH_FLOOR, {});
        this._setValueRef(node, 'a', source, socket);
        return node;
    }

    /** Add a Random data node (min–max, defaults 0–1). Returns the node index. */
    addRandomNode(min: number | { node: number; socket: string } = 0, max: number | { node: number; socket: string } = 1): number {
        const node = this._addNode(GraphOps.MATH_RANDOM, {});
        if (typeof min === 'number') this._setValue(node, 'min', min);
        else this._setValueRef(node, 'min', min.node, min.socket);
        if (typeof max === 'number') this._setValue(node, 'max', max);
        else this._setValueRef(node, 'max', max.node, max.socket);
        return node;
    }

    /** Add a Combine Vec3 (math/combine3) data node. Returns the node index. */
    addCombine3Node(aSource: number, bSource: number, cSource: number, socket = 'value'): number {
        const node = this._addNode(GraphOps.MATH_COMBINE3, {});
        this._setValueRef(node, 'a', aSource, socket);
        this._setValueRef(node, 'b', bSource, socket);
        this._setValueRef(node, 'c', cSource, socket);
        return node;
    }

    // ── Variables ──

    /** Declare a graph variable with an initial value. Returns the variable index. */
    addVariable(name: string, initialValue: number): number {
        const index = this._variables.length;
        this._variables.push({ name, type: 1, value: [initialValue] });
        return index;
    }

    // ── Flow ──

    /** Create a FlowChain from an existing node and output socket. */
    flowFrom(nodeIndex: number, socket: string): FlowChain {
        if (nodeIndex < 0 || nodeIndex >= this._nodes.length) {
            throw new RangeError(`flowFrom: nodeIndex ${nodeIndex} out of range [0, ${this._nodes.length})`);
        }
        return new FlowChain(this, nodeIndex, socket);
    }

    // ── Build ──

    /** Compile the builder into a GraphData object. */
    build(): GraphData {
        // Collect unique ops → declarations
        const opToDecl = new Map<string, number>();
        const declarations: Declaration[] = [];
        for (const node of this._nodes) {
            if (!opToDecl.has(node.op)) {
                opToDecl.set(node.op, declarations.length);
                declarations.push({ op: node.op });
            }
        }

        // Build graph nodes using original type indices (0=bool, 1=float, 2=string, 3=float3, 4=float4)
        const SIGNATURES = BUILDER_SIGNATURES;

        const graphNodes: GraphNode[] = this._nodes.map(node => {
            const declIndex = opToDecl.get(node.op) ?? 0;

            const configuration: Record<string, { type: number; value: unknown[] }> = {};
            for (const [key, value] of Object.entries(node.config)) {
                const v0 = value[0];
                const type = typeof v0 === 'boolean' ? 0 : typeof v0 === 'number' ? 1 : 2;
                configuration[key] = { type, value };
            }

            const values: Record<string, { type: number; value: unknown[] } | { node: number; socket: string }> = {};
            for (const [key, val] of Object.entries(node.values)) {
                values[key] = val;
            }

            const flows: Record<string, { node: number; socket: string }> = {};
            for (const [key, val] of Object.entries(node.flows)) {
                flows[key] = val;
            }

            return {
                declaration: declIndex,
                ...(Object.keys(configuration).length > 0 ? { configuration } : {}),
                ...(Object.keys(values).length > 0 ? { values } : {}),
                ...(Object.keys(flows).length > 0 ? { flows } : {}),
                // author metadata first; editor x/y stay authoritative (position is ours)
                metadata: { ...node.metadata, x: node.x, y: node.y },
            };
        });

        // Collect used type indices from value inputs and variables, then compact.
        // Config values are identity/addressing params — the runtime ignores their
        // type index, so they don't need to contribute types to the array.
        const usedIndices = new Set<number>();
        for (const node of graphNodes) {
            if (node.values) {
                for (const v of Object.values(node.values)) {
                    if ('type' in v) usedIndices.add(v.type);
                }
            }
        }
        for (const v of this._variables) usedIndices.add(v.type);

        const sorted = [...usedIndices].sort((a, b) => a - b);
        const remap = new Map<number, number>();
        sorted.forEach((old, compact) => remap.set(old, compact));

        // Remap type indices in-place
        for (const node of graphNodes) {
            if (node.configuration) {
                for (const c of Object.values(node.configuration)) c.type = remap.get(c.type) ?? 0;
            }
            if (node.values) {
                for (const v of Object.values(node.values)) {
                    if ('type' in v) v.type = remap.get(v.type) ?? 0;
                }
            }
        }

        return {
            types: sorted.map(i => ({ signature: SIGNATURES[i] })),
            declarations,
            variables: this._variables.map(v => ({ ...v, type: remap.get(v.type) ?? 0 })),
            events: this._events.map(e => ({ ...e })),
            nodes: graphNodes,
        };
    }

    // ── Internal helpers ──

    /** @internal */
    _addNode(op: string, config: Record<string, unknown[]>, metadata?: Record<string, unknown>): number {
        const index = this._nodes.length;
        this._nodes.push({
            op,
            config,
            values: {},
            flows: {},
            x: this._x,
            y: 0,
            ...(metadata ? { metadata } : {}),
        });
        this._x += 250;
        return index;
    }

    /** @internal */
    _setFlow(fromNode: number, fromSocket: string, toNode: number, toSocket = 'in'): void {
        this._nodes[fromNode].flows[fromSocket] = { node: toNode, socket: toSocket };
    }

    /** @internal */
    _setValue(nodeIndex: number, socket: string, value: unknown): void {
        const typeIndex = typeof value === 'boolean' ? 0 : typeof value === 'number' ? 1 : 2;
        this._nodes[nodeIndex].values[socket] = { type: typeIndex, value: [value] };
    }

    /** @internal Set a float2 (vec2) value on a node socket. */
    _setValueVec2(nodeIndex: number, socket: string, value: [number, number]): void {
        this._nodes[nodeIndex].values[socket] = { type: 5, value: [...value] };
    }

    /** @internal Set a float3 (vec3) value on a node socket. */
    _setValueVec3(nodeIndex: number, socket: string, value: [number, number, number]): void {
        this._nodes[nodeIndex].values[socket] = { type: 3, value: [...value] };
    }

    /** @internal Set a float4 (vec4/quaternion) value on a node socket. */
    _setValueVec4(nodeIndex: number, socket: string, value: [number, number, number, number]): void {
        this._nodes[nodeIndex].values[socket] = { type: 4, value: [...value] };
    }

    /** @internal */
    _setValueRef(nodeIndex: number, socket: string, sourceNode: number, sourceSocket: string): void {
        this._nodes[nodeIndex].values[socket] = { node: sourceNode, socket: sourceSocket };
    }

    /** @internal Append a variable of any builder-known signature; returns its index. */
    _addVariable(name: string, signature: TypeSignature, value: unknown): number {
        const type = BUILDER_SIGNATURES.indexOf(signature as typeof BUILDER_SIGNATURES[number]);
        if (type === -1) throw new Error(`GraphBuilder: unsupported variable type ${signature}`);
        this._variables.push({ name, type, value: [value] });
        return this._variables.length - 1;
    }

    /** @internal Append a value-less internal custom event; returns its index. */
    _addEvent(): number {
        this._events.push({ id: '' });
        return this._events.length - 1;
    }

    /** @internal */
    _setNodeY(nodeIndex: number, y: number): void {
        this._nodes[nodeIndex].y = y;
    }

    private _addEventNode(op: string, metadata?: Record<string, unknown>): FlowChain {
        const nodeIndex = this._addNode(op, {}, metadata);
        return new FlowChain(this, nodeIndex, 'out');
    }
}

// ============================================================================
// FlowChain — chains actions after an event or flow output
// ============================================================================

export class FlowChain {
    private _builder: GraphBuilder;
    private _prevNode: number;
    private _prevSocket: string;

    constructor(builder: GraphBuilder, prevNode: number, prevSocket: string) {
        this._builder = builder;
        this._prevNode = prevNode;
        this._prevSocket = prevSocket;
    }

    /**
     * Toggle an object's active state — the Toggle Active macro
     * (getActive → not → setActive), emitted tagged so the editor shows it as
     * one logical block rather than three loose nodes.
     */
    toggleActive(target: SceneObject): FlowChain {
        const { nodes } = expandMacro(this._builder, TOGGLE_ACTIVE_MACRO_DEF, {
            [TOGGLE_ACTIVE_TARGET_PARAM]: this._builder._targetPath(target),
        });
        this._wire(nodes['set']);
        return new FlowChain(this._builder, nodes['set'], 'out');
    }

    /** Set an object's active state. */
    setActive(target: SceneObject, state: boolean): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_ACTIVE, { target: [this._builder._targetPath(target)] });
        this._builder._setValue(node, 'state', state);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /**
     * Switch a RuntimeToggleable behavior (Billboard, Tether, ViewAnchor) on or off at
     * play time — "when tapped, start following". The behavior's authored
     * `enabled` field is the state at every play-mode start.
     */
    setBehaviorEnabled(target: SceneObject, behaviorType: string, enabled: boolean): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_BEHAVIOR_ENABLED, {
            target: [this._builder._targetPath(target)],
            behavior: [behaviorType],
        });
        this._builder._setValue(node, 'enabled', enabled);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Play an embedded animation clip. */
    playAnimation(target: SceneObject, clip?: string, options?: { loop?: boolean; speed?: number }): AnimationChain {
        const config: Record<string, unknown[]> = { target: [this._builder._targetPath(target)] };
        if (clip) config['clip'] = [clip];
        const node = this._builder._addNode(GraphOps.TRACE_PLAY_ANIMATION, config);
        if (options?.loop) this._builder._setValue(node, 'loop', true);
        if (options?.speed !== undefined) {
            this._builder._setValue(node, 'speed', options.speed);
        }
        this._wire(node);
        return new AnimationChain(this._builder, node);
    }

    /** Play an external animation GLB. */
    playAnimationExternal(target: SceneObject, src: string, clip?: string, options?: { loop?: boolean; speed?: number }): AnimationChain {
        const config: Record<string, unknown[]> = { target: [this._builder._targetPath(target)], src: [src] };
        if (clip) config['clip'] = [clip];
        const node = this._builder._addNode(GraphOps.TRACE_PLAY_ANIMATION_EXTERNAL, config);
        if (options?.loop) this._builder._setValue(node, 'loop', true);
        if (options?.speed !== undefined) {
            this._builder._setValue(node, 'speed', options.speed);
        }
        this._wire(node);
        return new AnimationChain(this._builder, node);
    }

    /**
     * Control video playback.
     * @deprecated Use the media controls (`playMedia`, `pauseMedia`, …) — they
     *   work uniformly on Video, AudioClip, Animation, and Pages targets, and
     *   `playMedia` additionally supports swapping the source URL at runtime.
     */
    playVideo(target: SceneObject, options?: { play?: boolean; volume?: number; seek?: number }): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_PLAY_VIDEO, { target: [this._builder._targetPath(target)] });
        if (options?.play !== undefined) this._builder._setValue(node, 'play', options.play);
        if (options?.volume !== undefined) this._builder._setValue(node, 'volume', options.volume);
        if (options?.seek !== undefined) this._builder._setValue(node, 'seek', options.seek);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /**
     * Play media on any Playable target (Video, AudioClip, Animation, Pages).
     *
     * - `src`: optional URL — only honored on targets that implement
     *   `PlayableSource` (Video, AudioClip). Ignored otherwise.
     * - `volume`: 0–1. Only honored on volume-capable targets (Video, AudioClip).
     * - `seek`: jump to this time in seconds before playing.
     */
    playMedia(target: SceneObject, options?: { src?: string; volume?: number; seek?: number }): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_PLAY_MEDIA, { target: [this._builder._targetPath(target)] });
        if (options?.src !== undefined) this._builder._setValue(node, 'src', options.src);
        if (options?.volume !== undefined) this._builder._setValue(node, 'volume', options.volume);
        if (options?.seek !== undefined) this._builder._setValue(node, 'seek', options.seek);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Pause media playback on any Playable target. */
    pauseMedia(target: SceneObject): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_PAUSE_MEDIA, { target: [this._builder._targetPath(target)] });
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Stop media playback and reset to time 0 on any Playable target. */
    stopMedia(target: SceneObject): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_STOP_MEDIA, { target: [this._builder._targetPath(target)] });
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Seek media to a specific time (seconds) without changing play/pause state. */
    seekMedia(target: SceneObject, time: number): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SEEK_MEDIA, { target: [this._builder._targetPath(target)] });
        this._builder._setValue(node, 'time', time);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Set media volume (0–1) on volume-capable targets. No-op on other Playables. */
    setMediaVolume(target: SceneObject, volume: number): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_MEDIA_VOLUME, { target: [this._builder._targetPath(target)] });
        this._builder._setValue(node, 'volume', volume);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Open a URL in a new tab. */
    navigate(url: string): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_NAVIGATE, {});
        this._builder._setValue(node, 'url', url);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Activate a child by index, deactivating siblings. */
    setActiveChild(target: SceneObject, index: number): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_ACTIVE_CHILD, { target: [this._builder._targetPath(target)] });
        this._builder._setValue(node, 'index', index);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Advance to the next page on a Pages container. */
    pagesNext(target: SceneObject, wrap = true, animate = false): FlowChain {
        const node = this._builder._addNode(GraphOps.PAGES_NEXT, { target: [this._builder._targetPath(target)] });
        this._builder._setValue(node, 'wrap', wrap);
        this._builder._setValue(node, 'animate', animate);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Go to the previous page on a Pages container. */
    pagesPrev(target: SceneObject, wrap = true, animate = false): FlowChain {
        const node = this._builder._addNode(GraphOps.PAGES_PREV, { target: [this._builder._targetPath(target)] });
        this._builder._setValue(node, 'wrap', wrap);
        this._builder._setValue(node, 'animate', animate);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /**
     * Shared emitter for the transform writers. No transition → a plain
     * `pointer/set` snap node. With a TimeTransition → a KHR-standard
     * `pointer/interpolate` node, compiled at EDIT TIME: `duration` rides as a
     * float input and the easing becomes cubic-bezier control-point inputs
     * `p1`/`p2` (named easing via EASING_CONTROL_POINTS, `cubic-bezier(...)`
     * string parsed; unknown → easeInOut). `{duration, easing}` never appears
     * in GraphData — the wire format stays flat and KHR-legal.
     */
    private _transformWrite(
        target: SceneObject,
        property: 'translation' | 'rotation' | 'scale',
        setValue: (node: number) => void,
        transition?: TimeTransition,
    ): FlowChain {
        const config = {
            target: [this._builder._targetPath(target)],
            property: [property],
        };
        const node = transition
            ? this._builder._addNode(GraphOps.POINTER_INTERPOLATE, config)
            : this._builder._addNode(GraphOps.POINTER_SET, config);
        setValue(node);
        if (transition) {
            const [x1, y1, x2, y2] = easingToControlPoints(transition.easing);
            this._builder._setValue(node, 'duration', transition.duration);
            this._builder._setValueVec2(node, 'p1', [x1, y1]);
            this._builder._setValueVec2(node, 'p2', [x2, y2]);
        }
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Set a target's position (translation), optionally eased via pointer/interpolate. */
    setPosition(target: SceneObject, value: [number, number, number], transition?: TimeTransition): FlowChain {
        return this._transformWrite(target, 'translation',
            node => this._builder._setValueVec3(node, 'value', value), transition);
    }

    /** Set a target's position from a data node output, optionally eased. */
    setPositionFrom(target: SceneObject, sourceNode: number, sourceSocket = 'value', transition?: TimeTransition): FlowChain {
        return this._transformWrite(target, 'translation',
            node => this._builder._setValueRef(node, 'value', sourceNode, sourceSocket), transition);
    }

    /** Set a target's rotation (quaternion [x, y, z, w]), optionally eased via pointer/interpolate. */
    setRotation(target: SceneObject, value: [number, number, number, number], transition?: TimeTransition): FlowChain {
        return this._transformWrite(target, 'rotation',
            node => this._builder._setValueVec4(node, 'value', value), transition);
    }

    /** Set a target's rotation from a data node output, optionally eased. */
    setRotationFrom(target: SceneObject, sourceNode: number, sourceSocket = 'value', transition?: TimeTransition): FlowChain {
        return this._transformWrite(target, 'rotation',
            node => this._builder._setValueRef(node, 'value', sourceNode, sourceSocket), transition);
    }

    /** Set a target's scale, optionally eased via pointer/interpolate. */
    setScale(target: SceneObject, value: [number, number, number], transition?: TimeTransition): FlowChain {
        return this._transformWrite(target, 'scale',
            node => this._builder._setValueVec3(node, 'value', value), transition);
    }

    /** Set a target's scale from a data node output, optionally eased. */
    setScaleFrom(target: SceneObject, sourceNode: number, sourceSocket = 'value', transition?: TimeTransition): FlowChain {
        return this._transformWrite(target, 'scale',
            node => this._builder._setValueRef(node, 'value', sourceNode, sourceSocket), transition);
    }

    /**
     * Set a named property on a target's Properties behavior.
     *
     * `transition` (optional) is a per-edge override: it wins over the property
     * declaration's default transition at the resolver. Omit to fall back to the
     * declaration default (or snap if the declaration has none).
     */
    setProperty(target: SceneObject, property: string, value: unknown, transition?: TimeTransition): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_PROPERTY, {
            target: [this._builder._targetPath(target)],
            property: [property],
            ...(transition ? { transition: [transition] } : {}),
        });
        this._builder._setValue(node, 'value', value);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /**
     * Set a named property on a target's Properties behavior,
     * with the value pulled from another node's output.
     */
    setPropertyFrom(target: SceneObject, property: string, sourceNode: number, sourceSocket = 'value', transition?: TimeTransition): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_PROPERTY, {
            target: [this._builder._targetPath(target)],
            property: [property],
            ...(transition ? { transition: [transition] } : {}),
        });
        this._builder._setValueRef(node, 'value', sourceNode, sourceSocket);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /**
     * Set the active state on `target` and its whole state group. `trace/setState`
     * is scoped at runtime (STATE_SCOPE_SPEC.md): it fans out to every participating
     * descendant, pruned at nested `stateGroup` boundaries. Target the group owner
     * to switch the group; empty string reverts to base. One node — no per-member
     * chain.
     */
    setState(target: SceneObject, state: string): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_STATE, {
            target: [this._builder._targetPath(target)],
        });
        this._builder._setValue(node, 'state', state);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Branch on a boolean condition. Returns true/false FlowChains. */
    branch(conditionNode: number, conditionSocket = 'value'): BranchChain {
        const node = this._builder._addNode(GraphOps.FLOW_BRANCH, {});
        this._builder._setValueRef(node, 'condition', conditionNode, conditionSocket);
        this._wire(node);
        return new BranchChain(this._builder, node);
    }

    /** Execute multiple flow outputs in sequence. Returns indexed FlowChains. */
    sequence(count = 2): SequenceChain {
        const node = this._builder._addNode(GraphOps.FLOW_SEQUENCE, {});
        this._wire(node);
        return new SequenceChain(this._builder, node, count);
    }

    /**
     * Fire the chained action every `seconds`, until cancelled. Emits the
     * Interval canned composition — standard `flow/sequence`,
     * `flow/setDelay` and `event/send`/`event/receive` nodes over a private
     * internal event, tagged with macro metadata, no custom op, no flow cycle
     * (see macros/interval.ts). `.whenDone` chains the per-period payload;
     * `.then` continues immediately; `cancelInterval()` on another flow stops
     * the loop.
     *
     * Fire-then-re-arm semantics: each period the payload fires, then the
     * delay re-arms — the period includes frame-delivery time.
     */
    interval(seconds: number): IntervalChain {
        // Authoring-time guard: a 0-second delay self-loop live-locks the
        // frame (Babylon's Observable visits observers appended mid-
        // notification, so the loop re-fires synchronously and never yields).
        // Reject here — explicit beats a silent clamp.
        if (!(seconds > 0) || !Number.isFinite(seconds)) {
            throw new RangeError(`interval(${seconds}): period must be a positive, finite number of seconds`);
        }
        const nodes = expandIntervalMacro(this._builder, seconds);
        this._wire(nodes.restart);
        return new IntervalChain(this._builder, nodes);
    }

    /**
     * Stop a running Interval by driving this flow into the macro's `cancel`
     * input — the write that clears its running flag, so the next delivery is
     * dropped and the loop ends. Terminal — wiring runs into the existing
     * expansion, so there is no downstream chain to return (same shape as
     * `cancelPendingDelay`).
     */
    cancelInterval(interval: IntervalChain): void {
        if (interval.and !== this._builder) {
            throw new Error('cancelInterval: IntervalChain belongs to a different GraphBuilder');
        }
        this._builder._setFlow(this._prevNode, this._prevSocket, interval.nodes.clear, 'in');
    }

    /**
     * Wait `seconds`, then continue. `out` fires immediately; `done` fires
     * after the delay. Use `.whenDone` to chain the post-delay action.
     */
    delay(seconds: number): DelayChain {
        const node = this._builder._addNode(GraphOps.FLOW_SET_DELAY, {});
        this._builder._setValue(node, 'duration', seconds);
        this._wire(node);
        return new DelayChain(this._builder, node);
    }

    /**
     * Cancel a specific pending delay. Pass the `DelayChain` returned by an
     * earlier `delay()` — its `lastDelay` output is wired into the cancel
     * node's `delay` input.
     *
     * Takes a `DelayChain`, not a raw node index: indices are only meaningful
     * within a single builder, so accepting one would let a stale or
     * cross-builder index silently miswire (see the SceneObject-identity rule
     * in AGENTS.md). For the rare advanced case, `DelayChain.node` + the raw
     * `flowFrom`/`_setValueRef` escape hatches remain available.
     */
    cancelDelay(delay: DelayChain): FlowChain {
        if (delay.and !== this._builder) {
            throw new Error('cancelDelay: DelayChain belongs to a different GraphBuilder');
        }
        const node = this._builder._addNode(GraphOps.FLOW_CANCEL_DELAY, {});
        // KHR socket names — compiler remaps both to Babylon indices via DATA_SOCKET_REMAP.
        this._builder._setValueRef(node, 'delay', delay.node, 'lastDelay');
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /**
     * Abort a pending delay by driving this flow into the delay node's own
     * `cancel` input. Terminal — wiring runs into the existing delay node, so
     * there is no downstream chain to return.
     *
     * This is the node-level `cancel` flow input, distinct from `cancelDelay()`
     * which adds a separate Cancel Delay node targeting a delay by `lastDelay`
     * reference. Node-level cancel drops ALL of that delay node's pending
     * timers; `cancelDelay()` cancels one specific scheduled delay.
     */
    cancelPendingDelay(delay: DelayChain): void {
        if (delay.and !== this._builder) {
            throw new Error('cancelPendingDelay: DelayChain belongs to a different GraphBuilder');
        }
        if (delay.node === this._prevNode) {
            // Wiring a delay's own out/done back into its cancel input is a
            // self-loop (immediate completion cancels itself) — a broken graph.
            throw new Error('cancelPendingDelay: cannot wire a delay node into its own cancel input');
        }
        this._builder._setFlow(this._prevNode, this._prevSocket, delay.node, 'cancel');
    }

    /** Activate a child by index from a data node, deactivating siblings. */
    setActiveChildFrom(target: SceneObject, sourceNode: number, sourceSocket = 'value'): FlowChain {
        const node = this._builder._addNode(GraphOps.TRACE_SET_ACTIVE_CHILD, { target: [this._builder._targetPath(target)] });
        this._builder._setValueRef(node, 'index', sourceNode, sourceSocket);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Set a graph variable. */
    setVariable(variableIndex: number, value: number): FlowChain {
        const node = this._builder._addNode(GraphOps.VARIABLE_SET, { variable: [variableIndex] });
        this._builder._setValue(node, 'value', value);
        this._wire(node);
        return new FlowChain(this._builder, node, 'out');
    }

    /** Return to the parent builder for adding more events. */
    get and(): GraphBuilder {
        return this._builder;
    }

    /** Alias for .and — return to builder and build. */
    build(): GraphData {
        return this._builder.build();
    }

    private _wire(targetNode: number): void {
        this._builder._setFlow(this._prevNode, this._prevSocket, targetNode);
    }
}

// ============================================================================
// AnimationChain — returned by playAnimation, allows .thenPlay chaining
// ============================================================================

export class AnimationChain {
    private _builder: GraphBuilder;
    private _node: number;

    constructor(builder: GraphBuilder, node: number) {
        this._builder = builder;
        this._node = node;
    }

    /** Chain another animation after this one finishes. */
    thenPlay(target: SceneObject, clip?: string, options?: { loop?: boolean; speed?: number }): AnimationChain {
        const config: Record<string, unknown[]> = { target: [this._builder._targetPath(target)] };
        if (clip) config['clip'] = [clip];
        const node = this._builder._addNode(GraphOps.TRACE_PLAY_ANIMATION, config);
        if (options?.loop) this._builder._setValue(node, 'loop', true);
        if (options?.speed !== undefined) {
            this._builder._setValue(node, 'speed', options.speed);
        }
        this._builder._setFlow(this._node, 'done', node);
        return new AnimationChain(this._builder, node);
    }

    /** Continue the flow after this animation's immediate out. */
    get then(): FlowChain {
        return new FlowChain(this._builder, this._node, 'out');
    }

    /** Continue the flow after this animation finishes. */
    get whenDone(): FlowChain {
        return new FlowChain(this._builder, this._node, 'done');
    }

    /** Return to the parent builder. */
    get and(): GraphBuilder {
        return this._builder;
    }

    build(): GraphData {
        return this._builder.build();
    }
}

// ============================================================================
// DelayChain — returned by delay(), splits the immediate out from the delayed done
// ============================================================================

export class DelayChain {
    private _builder: GraphBuilder;
    private _node: number;

    constructor(builder: GraphBuilder, node: number) {
        this._builder = builder;
        this._node = node;
    }

    /**
     * Graph node index of this delay — pass to `cancelDelay()` to cancel it.
     * Guard with `=== undefined`, not truthiness: node 0 is valid but falsy.
     */
    get node(): number {
        return this._node;
    }

    /** Continue immediately, in parallel with the running delay (flow `out`). */
    get then(): FlowChain {
        return new FlowChain(this._builder, this._node, 'out');
    }

    /** Continue after the delay elapses (flow `done`). */
    get whenDone(): FlowChain {
        return new FlowChain(this._builder, this._node, 'done');
    }

    /** Return to the parent builder. */
    get and(): GraphBuilder {
        return this._builder;
    }

    build(): GraphData {
        return this._builder.build();
    }
}

// ============================================================================
// IntervalChain — returned by interval(), wraps the macro expansion
// ============================================================================

export class IntervalChain {
    private _builder: GraphBuilder;
    private _nodes: IntervalMacroNodes;

    constructor(builder: GraphBuilder, nodes: IntervalMacroNodes) {
        this._builder = builder;
        this._nodes = nodes;
    }

    /**
     * Node indices of the underlying expansion — pass to `cancelInterval()`,
     * or use directly with the raw `flowFrom`/`_setFlow` escape hatches.
     */
    get nodes(): IntervalMacroNodes {
        return this._nodes;
    }

    /** Continue immediately, in parallel with the running interval. */
    get then(): FlowChain {
        return new FlowChain(this._builder, this._nodes.restart, INTERVAL_CONTINUE_SOCKET);
    }

    /** Chain the payload that fires once every period. */
    get whenDone(): FlowChain {
        return new FlowChain(this._builder, this._nodes.tick, INTERVAL_DONE_SOCKET);
    }

    /** Return to the parent builder. */
    get and(): GraphBuilder {
        return this._builder;
    }

    build(): GraphData {
        return this._builder.build();
    }
}

// ============================================================================
// HoverChain — provides enter/exit sub-chains
// ============================================================================

export class HoverChain {
    private _builder: GraphBuilder;
    private _inNode: number;
    private _outNode: number;

    constructor(builder: GraphBuilder, inNode: number, outNode: number) {
        this._builder = builder;
        this._inNode = inNode;
        this._outNode = outNode;
    }

    /** Chain actions on hover enter (event/onHoverIn). */
    enter(): FlowChain {
        return new FlowChain(this._builder, this._inNode, 'out');
    }

    /** Chain actions on hover exit (event/onHoverOut). */
    exit(): FlowChain {
        return new FlowChain(this._builder, this._outNode, 'out');
    }

    /** Return to the parent builder. */
    get and(): GraphBuilder {
        return this._builder;
    }
}

// ============================================================================
// ProximityChain — provides enter/exit sub-chains
// ============================================================================

export class ProximityChain {
    private _builder: GraphBuilder;
    private _node: number;

    constructor(builder: GraphBuilder, node: number) {
        this._builder = builder;
        this._node = node;
    }

    /** Chain actions on proximity enter. */
    enter(): FlowChain {
        return new FlowChain(this._builder, this._node, 'enter');
    }

    /** Chain actions on proximity exit. */
    exit(): FlowChain {
        return new FlowChain(this._builder, this._node, 'exit');
    }

    /** Return to the parent builder. */
    get and(): GraphBuilder {
        return this._builder;
    }
}

// ============================================================================
// BranchChain — provides true/false sub-chains
// ============================================================================

export class BranchChain {
    private _builder: GraphBuilder;
    private _node: number;

    constructor(builder: GraphBuilder, node: number) {
        this._builder = builder;
        this._node = node;
    }

    /** Chain actions for the true branch. */
    onTrue(): FlowChain {
        return new FlowChain(this._builder, this._node, 'true');
    }

    /** Chain actions for the false branch. */
    onFalse(): FlowChain {
        return new FlowChain(this._builder, this._node, 'false');
    }

    /** Return to the parent builder. */
    get and(): GraphBuilder {
        return this._builder;
    }
}

// ============================================================================
// SequenceChain — provides indexed sub-chains
// ============================================================================

export class SequenceChain {
    private _builder: GraphBuilder;
    private _node: number;
    private _count: number;

    constructor(builder: GraphBuilder, node: number, count: number) {
        this._builder = builder;
        this._node = node;
        this._count = count;
    }

    /** Chain actions for the i-th output (0-based). */
    step(index: number): FlowChain {
        if (index < 0 || index >= this._count) {
            throw new RangeError(`SequenceChain.step: index ${index} out of range [0, ${this._count})`);
        }
        return new FlowChain(this._builder, this._node, String(index));
    }

    /** Return to the parent builder. */
    get and(): GraphBuilder {
        return this._builder;
    }
}
